function updatePebbleProductsProgress(swiper, thumb, track) {
  if (!swiper || !thumb) return;

  const trackWidth = track?.clientWidth || 200;
  const slides = swiper.slides?.length || 0;

  if (slides <= 1) {
    thumb.style.width = '100%';
    thumb.style.transform = 'translateX(0)';
    return;
  }

  const perView = visibleSlidesPerView(swiper);
  const ratio = Math.min(perView / slides, 1);
  const maxIndex = Math.max(slides - Math.ceil(perView), 1);
  const progress = swiper.activeIndex / maxIndex;
  const thumbWidth = Math.max(trackWidth * ratio, 24);

  thumb.style.width = `${thumbWidth}px`;
  thumb.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
}

function visibleSlidesPerView(swiper) {
  if (typeof swiper?.params?.slidesPerView === 'number') {
    return swiper.params.slidesPerView;
  }
  const slide = swiper?.slides?.[0];
  const slideWidth = slide?.offsetWidth || 0;
  const viewport = swiper?.width || 0;
  if (!slideWidth || !viewport) return 1;
  const gap = Number(swiper.params?.spaceBetween) || 0;
  return Math.max(1, viewport / (slideWidth + gap));
}

function disableNativeDrag(root) {
  if (!root || root.pebbleNativeDragOff) return;
  root.pebbleNativeDragOff = true;

  root.addEventListener('dragstart', (event) => {
    event.preventDefault();
  });

  root.querySelectorAll('a, img').forEach((el) => {
    el.setAttribute('draggable', 'false');
  });
}

const PRODUCT_CAROUSEL_CLICK_THRESHOLD = 12;

function applyProductCarouselClickParams(swiper) {
  if (!swiper?.params) return;
  swiper.allowTouchMove = true;
  swiper.allowClick = true;
  swiper.params.simulateTouch = true;
  swiper.params.grabCursor = true;
  swiper.params.preventClicks = false;
  swiper.params.preventClicksPropagation = false;
  swiper.params.slideToClickedSlide = false;
  swiper.params.slidesPerView = 'auto';
  swiper.params.threshold = PRODUCT_CAROUSEL_CLICK_THRESHOLD;
  swiper.params.touchStartPreventDefault = false;
  swiper.params.shortSwipes = true;
  if (swiper.params.breakpoints?.[780]) {
    swiper.params.breakpoints[780].slidesPerView = 'auto';
    swiper.params.breakpoints[780].slideToClickedSlide = false;
    swiper.params.breakpoints[780].threshold = PRODUCT_CAROUSEL_CLICK_THRESHOLD;
  }
}

function bindProductCardClickVsDrag(root, swiper) {
  if (!root || root.pebbleClickVsDragBound) return;
  root.pebbleClickVsDragBound = true;

  let startX = 0;
  let startY = 0;
  let dragging = false;

  root.addEventListener('pointerdown', (event) => {
    startX = event.clientX;
    startY = event.clientY;
    dragging = false;
    if (swiper) {
      swiper.params.slideToClickedSlide = false;
      swiper.allowClick = true;
    }
  }, true);

  root.addEventListener('pointermove', (event) => {
    if (!(event.buttons & 1)) return;
    if (Math.abs(event.clientX - startX) > PRODUCT_CAROUSEL_CLICK_THRESHOLD
      || Math.abs(event.clientY - startY) > PRODUCT_CAROUSEL_CLICK_THRESHOLD) {
      dragging = true;
      if (swiper) swiper.allowClick = false;
    }
  }, true);

  root.addEventListener('click', (event) => {
    if (!dragging) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);
}

function lockHorizontalScrollOnFocus(scroller) {
  if (!scroller || scroller.pebbleFocusScrollLocked) return;
  scroller.pebbleFocusScrollLocked = true;

  let saved = scroller.scrollLeft || 0;

  scroller.addEventListener('pointerdown', () => {
    saved = scroller.scrollLeft || 0;
  }, true);

  scroller.addEventListener('focusin', () => {
    scroller.scrollLeft = saved;
    window.requestAnimationFrame(() => {
      scroller.scrollLeft = saved;
    });
  });
}

function bindPebbleProductsControls(tab, swiper) {
  if (!tab || !swiper) return false;
  if (tab.pebbleProductsBound === swiper) return true;
  tab.pebbleProductsBound = swiper;

  applyProductCarouselClickParams(swiper);
  if (typeof swiper.setGrabCursor === 'function') {
    swiper.setGrabCursor();
  }
  if (typeof swiper.update === 'function' && !tab.pebbleProductsUpdated) {
    tab.pebbleProductsUpdated = true;
    try {
      swiper.update();
    } catch (_) {
      /* ignore */
    }
  }

  const sliderRoot = tab.querySelector('.products--slider') || tab;
  bindProductCardClickVsDrag(sliderRoot, swiper);
  lockHorizontalScrollOnFocus(tab.querySelector('.swiper') || sliderRoot);

  const thumb = tab.querySelector('.pebble-products__progress-thumb');
  const track = tab.querySelector('.pebble-products__progress');
  const prev = tab.querySelector('[data-pebble-products-prev]');
  const next = tab.querySelector('[data-pebble-products-next]');

  const refresh = () => {
    updatePebbleProductsProgress(swiper, thumb, track);
    if (prev) prev.disabled = swiper.isBeginning;
    if (next) next.disabled = swiper.isEnd;
  };

  swiper.on('slideChange', refresh);
  swiper.on('resize', refresh);
  swiper.on('update', refresh);
  swiper.on('breakpoint', () => {
    applyProductCarouselClickParams(swiper);
    refresh();
  });

  if (prev && !prev.pebbleProductsBound) {
    prev.pebbleProductsBound = true;
    prev.addEventListener('click', () => swiper.slidePrev());
  }

  if (next && !next.pebbleProductsBound) {
    next.pebbleProductsBound = true;
    next.addEventListener('click', () => swiper.slideNext());
  }

  if (track && !track.pebbleProductsSeekBound) {
    track.pebbleProductsSeekBound = true;
    track.style.cursor = 'pointer';
    track.addEventListener('click', (event) => {
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      const slides = swiper.slides?.length || 0;
      const perView = visibleSlidesPerView(swiper);
      const maxIndex = Math.max(slides - Math.ceil(perView), 0);
      swiper.slideTo(Math.round(ratio * maxIndex));
    });
  }

  refresh();
  return true;
}

function waitForPebbleSwiper(swiperEl, onReady) {
  if (!swiperEl || typeof onReady !== 'function') return;

  if (swiperEl.swiper) {
    onReady(swiperEl.swiper);
    return;
  }

  if (swiperEl.pebbleSwiperWaiters) {
    swiperEl.pebbleSwiperWaiters.push(onReady);
    return;
  }

  swiperEl.pebbleSwiperWaiters = [onReady];

  const flush = () => {
    const swiper = swiperEl.swiper;
    if (!swiper) return false;
    const waiters = swiperEl.pebbleSwiperWaiters || [];
    swiperEl.pebbleSwiperWaiters = null;
    cleanup();
    waiters.forEach((fn) => fn(swiper));
    return true;
  };

  const io = new IntersectionObserver(() => {
    flush();
  }, { rootMargin: '0px 0px 400px 0px' });
  io.observe(swiperEl);

  const interval = window.setInterval(flush, 120);

  function cleanup() {
    window.clearInterval(interval);
    io.disconnect();
  }

  window.setTimeout(cleanup, 180000);
}

function refreshPebbleSwiper(swiperEl) {
  if (!swiperEl) return null;

  if (swiperEl.swiper) {
    try {
      swiperEl.swiper.update();
      swiperEl.swiper.updateSize();
      swiperEl.swiper.updateSlides();
      swiperEl.swiper.slideTo(0, 0);
    } catch (_) {
      /* ignore */
    }
    return swiperEl.swiper;
  }

  if (swiperEl.pebbleIniting) return swiperEl.swiper || null;
  swiperEl.pebbleIniting = true;

  if (typeof swiperEl.settings === "function") {
    try {
      swiperEl.settings();
    } catch (_) {
      swiperEl.pebbleIniting = false;
    }
  } else if (typeof swiperEl.init === "function") {
    try {
      swiperEl.init();
    } catch (_) {
      swiperEl.pebbleIniting = false;
    }
  }

  return swiperEl.swiper || null;
}

function whenTabLaidOut(tab, onReady) {
  if (!tab || typeof onReady !== "function") return;
  let tries = 0;

  const run = () => {
    tries += 1;
    const el = tab.querySelector("swiper-slider") || tab;
    const width = el.getBoundingClientRect().width;
    if (width >= 80 || tries > 20) {
      onReady(tab);
      return;
    }
    window.requestAnimationFrame(run);
  };

  window.requestAnimationFrame(run);
}

function bindPebbleProductsTab(tab) {
  if (!tab) return;

  const swiperEl = tab.querySelector('swiper-slider');
  if (!swiperEl) return;

  disableNativeDrag(tab.querySelector('.products--slider') || tab);

  waitForPebbleSwiper(swiperEl, (swiper) => {
    bindPebbleProductsControls(tab, swiper);
  });
}

function activatePebbleProductsTab(tab) {
  if (!tab) return;
  whenTabLaidOut(tab, () => {
    const swiperEl = tab.querySelector('swiper-slider');
    const swiper = refreshPebbleSwiper(swiperEl);
    if (swiper) bindPebbleProductsControls(tab, swiper);
    else bindPebbleProductsTab(tab);
  });
}

function bindProductCarouselSwiper(swiperEl) {
  if (!swiperEl || !swiperEl.querySelector('.product-card')) return;

  disableNativeDrag(swiperEl.closest('.products--slider') || swiperEl);

  waitForPebbleSwiper(swiperEl, (swiper) => {
    applyProductCarouselClickParams(swiper);
    bindProductCardClickVsDrag(swiperEl.closest('.products--slider') || swiperEl, swiper);
    lockHorizontalScrollOnFocus(swiperEl);
    const tab = swiperEl.closest('.section-tab');
    if (tab) bindPebbleProductsControls(tab, swiper);
  });
}

function initPebbleProductsSection(section) {
  section.querySelectorAll('.section-tab').forEach((tab) => {
    if (tab.classList.contains('active')) activatePebbleProductsTab(tab);
    else bindPebbleProductsTab(tab);
  });
  section.querySelectorAll('swiper-slider').forEach(bindProductCarouselSwiper);

  section.querySelectorAll('.section-tab swiper-slider').forEach((swiperEl) => {
    if (swiperEl.pebbleProductsIoBound) return;
    swiperEl.pebbleProductsIoBound = true;

    const io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      const tab = swiperEl.closest('.section-tab');
      if (tab?.classList.contains('active')) activatePebbleProductsTab(tab);
    }, { rootMargin: '0px 0px 400px 0px' });

    io.observe(swiperEl);
  });

  section.querySelectorAll('[data-tab-handle]').forEach((tabBtn) => {
    if (tabBtn.pebbleProductsTabBound) return;
    tabBtn.pebbleProductsTabBound = true;

    tabBtn.addEventListener('click', () => {
      const handle = tabBtn.getAttribute('data-tab-handle');
      const tabsRoot = tabBtn.closest('.pebble-products__tabs') || tabBtn.parentElement;
      if (handle && tabsRoot) {
        tabsRoot.querySelectorAll('[data-tab-handle]').forEach((item) => {
          const isCurrent = item === tabBtn;
          item.classList.toggle('active', isCurrent);
          item.classList.toggle('is-active', isCurrent);
          if (item.getAttribute('role') === 'tab') {
            item.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
          }
        });
        section.querySelectorAll('[data-tab-content].active').forEach((panel) => {
          panel.classList.remove('active');
        });
        const target = section.querySelector('[data-tab-content="' + handle + '"]');
        if (target) target.classList.add('active');
      }

      tabBtn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      window.setTimeout(() => {
        const activeTab = section.querySelector('.section-tab.active');
        if (activeTab) activatePebbleProductsTab(activeTab);
      }, 50);
    });
  });
}

function initPebbleProducts() {
  document.querySelectorAll('.featured-products-2--pebble').forEach(initPebbleProductsSection);
  document.querySelectorAll('swiper-slider').forEach(bindProductCarouselSwiper);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPebbleProducts);
} else {
  initPebbleProducts();
}

document.addEventListener('shopify:section:load', (event) => {
  const pebble = event.target.querySelector('.featured-products-2--pebble');
  if (pebble) initPebbleProductsSection(pebble);
  event.target.querySelectorAll?.('swiper-slider').forEach(bindProductCarouselSwiper);
  if (event.target.matches?.('swiper-slider')) bindProductCarouselSwiper(event.target);
});
